<template>
  <span class="action-button">
    <slot name="trigger" :open="openModal">
      <CButton color="secondary" size="sm" @click="openModal()" title="Log de alterações" :disabled="!chave" :name="chave">
        <fa-icon icon="clipboard-list" />
      </CButton>
    </slot>

    <modal :open="true" :name="modalName" height="auto" width="94%" :adaptive="true" :maxWidth="unified ? 1100 : 1080" :lazy="true" :scrollable="true" :clickToClose="false">
      <CloseModal :close="closeModal" />
      <div class="modalbox">
        <CTabs variant="tabs" class="nav-tabs-boxed">
          <CTab title="Log de alterações">
            <div v-if="isFetching" class="">
              <LoadingMessage message="Carregando, aguarde" is-active align-center />
            </div>
            <div v-else-if="errorMessage" class="alert alert-danger">
              <pre>{{ errorMessage }}</pre>
            </div>

            <!-- Visualização unificada (diff por campo) -->
            <template v-else-if="unified">
              <div v-if="!result.itens || result.itens.length === 0" class="alert alert-info">
                Nenhuma alteração registrada para este registro.
              </div>
              <div v-else class="unified-log">
                <div v-for="(item, idx) in result.itens" :key="idx" class="log-entry">
                  <div class="log-entry-header">
                    <div class="log-entry-meta">
                      <span class="badge" :class="acaoBadgeClass(item.acao)">{{ item.acao }}</span>
                      <span class="log-user">
                        <fa-icon icon="user" /> {{ item.colaborador && item.colaborador.nome || '—' }}
                      </span>
                    </div>
                    <span class="log-datetime">
                      <fa-icon icon="clock" /> {{ toDateTime(item.datahora_criacao) }}
                    </span>
                  </div>
                  <UnifiedDiffTable
                    :antes="item.dados_anteriores || {}"
                    :novos="item.dados_novos || {}"
                    :labels="fieldLabels"
                    :resolver="resolver"
                    :resolvable-fields="resolvableFields"
                  />
                </div>
                <pagination
                  :data="result.paginator"
                  :limit="2"
                  @pagination-change-page="getLogs"
                />
              </div>
            </template>

            <!-- Visualização em linha do tempo (timeline) -->
            <LogAlteracoesTimeline
              v-else
              :itens="result.itens || []"
              :paginator="result.paginator || {}"
              :total="result.paginator ? result.paginator.total : null"
              :subtitulo="subtituloRegistro"
              :restorable="restorable"
              :field-labels="fieldLabels"
              @restaurar="restaurar"
              @page="getLogs"
            />
          </CTab>
        </CTabs>
      </div>
    </modal>
  </span>
</template>

<script>

import CloseModal from "@/components/CloseModal";
import UnifiedDiffTable from "@/components/Log/UnifiedDiffTable.vue";
import LogAlteracoesTimeline from "@/components/Log/LogAlteracoesTimeline.vue";
import {get} from "@/helpers/apiRequest";
import {toDateTime} from "@/helpers/utils";

export default {
  name: "LogAlteracaoDados",
  props: {
    tabela: { type: String, required: true },
    chave: { type: [String, Number], default: null },
    unified: { type: Boolean, default: false },
    fieldLabels: { type: Object, default: () => ({}) },
    resolver: { type: Function, default: null },
    resolvableFields: { type: Array, default: () => [] },
    // Habilita o botão "Restaurar" (emite @restaurar com o SQL escolhido).
    restorable: { type: Boolean, default: false },
    // Sufixo opcional para o nome do modal — evita colisão quando há duas
    // instâncias para a mesma tabela/chave na mesma tela (ex.: lista + editor).
    instancia: { type: String, default: "" },
    // Subtítulo do registro auditado (ex.: "Campanha #2 · Henkel"). Cai num
    // texto derivado de tabela/chave quando não informado.
    registroLabel: { type: String, default: "" },
  },
  components: {
    CloseModal,
    UnifiedDiffTable,
    LogAlteracoesTimeline,
  },
  data() {
    return {
      errorMessage: null,
      isFetching: false,
      result: {},
    }
  },
  computed: {
    modalName() {
      const sufixo = this.instancia ? `-${this.instancia}` : "";
      return `log-alteracao-dados-${this.tabela}-${this.chave}${sufixo}`;
    },
    subtituloRegistro() {
      const base = this.registroLabel || `${this.tabela} · ${this.chave}`;
      return `${base} — auditoria de todas as edições do registro`;
    },
  },
  methods: {
    toDateTime,
    getLogs(page = 1) {
      this.isFetching = true;
      this.errorMessage = null;
      let tabela = encodeURI(this.tabela);
      let chave  = encodeURI(this.chave);

      get(`/admin/log/alteracao-dados/?tabela=${tabela}&chave=${chave}&page=${page}`)
        .then((response) => {
          this.result = response;
        })
        .catch((error) => this.errorMessage = error.message)
        .finally(() => {
          this.isFetching = false;
        })
    },
    openModal() {
      this.getLogs(1);
      this.$modal.show(this.modalName);
    },
    closeModal() {
      this.$modal.hide(this.modalName);
    },
    acaoBadgeClass(acao) {
      switch ((acao || '').toUpperCase()) {
        case 'CREATE': return 'badge-success';
        case 'DELETE': return 'badge-danger';
        case 'UPDATE': return 'badge-primary';
        default: return 'badge-secondary';
      }
    },
    // Emite o SQL escolhido para o componente pai carregar no editor (revisão + novo save).
    restaurar(sql) {
      this.$emit('restaurar', sql);
      this.closeModal();
    },
  }
}
</script>

<style scoped>
  span.action-button {
    display: inline-block;
  }

  .unified-log {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .log-entry {
    border: 1px solid #e4e7eb;
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
  }

  .log-entry-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
    font-size: 12px;
  }

  .log-entry-meta {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .log-user {
    color: #344054;
    font-weight: 500;
  }

  .log-datetime {
    color: #667085;
  }
</style>
